import { faClipboardCheck } from '@fortawesome/pro-solid-svg-icons';
import {
  GenericSelectorModalConfig,
  GenericNodeComponentConfig,
} from '../shared';

/**
 * CRMStatus item interface for ui_metadata storage
 */
export interface CRMStatusItem {
  id: string;
  displayLabel: string;
  crm_type: string;
  crm_status: string | null;
}

/**
 * Converts a snake_case status value to a readable format.
 * Groups consecutive single-letter parts into acronyms (e.g. h_c_p_adoption → HCP Adoption).
 */
export const formatSnakeCaseStatus = (value: string): string => {
  const parts = value.split('_');
  const result: string[] = [];
  let acronym = '';

  for (const part of parts) {
    if (part.length === 1) {
      acronym += part.toUpperCase();
    } else {
      if (acronym) {
        result.push(acronym);
        acronym = '';
      }
      result.push(part.charAt(0).toUpperCase() + part.slice(1));
    }
  }

  if (acronym) {
    result.push(acronym);
  }

  return result.join(' ');
};

/**
 * Get display label for a CRM status
 */
export const getCRMStatusDisplayLabel = (item: CRMStatusItem): string => {
  return item.displayLabel || (item.crm_status ? formatSnakeCaseStatus(item.crm_status) : '(No Status)');
};

/**
 * Generate composite ID for CRM status
 */
export const getCRMStatusId = (crm_type: string, crm_status: string | null): string => {
  return `${crm_type}-${crm_status || 'null'}`;
};

/**
 * Transform API response to CRMStatusItem format
 * @param statusLabelMap - Optional map from status value to label (from MedRM field definitions)
 */
export const transformCRMStatus = (
  apiItem: {
    crm_type: string;
    crm_status: string | null;
  },
  statusLabelMap?: Map<string, string>
): CRMStatusItem => {
  const id = getCRMStatusId(apiItem.crm_type, apiItem.crm_status);
  const mappedLabel =
    apiItem.crm_status && statusLabelMap?.get(apiItem.crm_status);
  return {
    id,
    displayLabel: mappedLabel || apiItem.crm_status || '(No Status)',
    crm_type: apiItem.crm_type,
    crm_status: apiItem.crm_status,
  };
};

/**
 * Modal configuration for CRMStatus selector
 */
export const crmStatusModalConfig: GenericSelectorModalConfig<CRMStatusItem> = {
  title: 'Select CRM Statuses',
  searchPlaceholder: 'Filter CRM statuses...',
  emptyStateIcon: faClipboardCheck,
  emptyStateMessage: 'No CRM statuses selected',
  noResultsMessage: (searchTerm) => `No CRM statuses match "${searchTerm}"`,
  colorTheme: 'teal',
  selectionMode: 'multiple',
  showConnectorToggle: true,
  defaultConnector: 'ANY',
  getItemLabel: (item) => getCRMStatusDisplayLabel(item),
  getItemKey: (item) => item.id,
  isItemSelected: (item, selectedItems) =>
    selectedItems.some((s) => s.id === item.id),
  itemLabel: 'CRM status',
  itemLabelPlural: 'CRM statuses',
};

/**
 * Node display configuration for CRMStatus
 */
export const crmStatusNodeConfig: GenericNodeComponentConfig<CRMStatusItem> = {
  colorTheme: 'teal',
  getItemLabel: (item) => getCRMStatusDisplayLabel(item),
  getItemKey: (item) => item.id,
  maxVisibleItems: 2,
  showConnectorLabel: true,
};
